import { useEffect, useState } from "react";
import { api, fmtINR, monthISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import { Save, Loader2, Trash2, Plus } from "lucide-react";

const METRICS = [
  ["revenue_monthly", "Revenue (₹)", true],
  ["new_partners_monthly", "New Partners"],
  ["partner_visits_monthly", "Partner Visits"],
  ["referrals_generated_monthly", "Referrals Generated"],
  ["referrals_converted_monthly", "Referrals Converted"],
  ["crm_entries_monthly", "CRM Entries"],
  ["disputes_monthly", "Commission Disputes"],
  ["reports_monthly", "Reports Submitted"],
  ["events_monthly", "Events Organized"],
  ["partners_at_event_monthly", "Partners at Event"],
  ["discipline_monthly", "10-Point Discipline Score"],
];

const emptyTarget = {
  revenue_monthly: 0, new_partners_monthly: 0, partner_visits_monthly: 0,
  referrals_generated_monthly: 0, referrals_converted_monthly: 0,
  crm_entries_monthly: 0, disputes_monthly: 0, reports_monthly: 1,
  events_monthly: 1, partners_at_event_monthly: 3, discipline_monthly: 6,
};

export default function Targets() {
  const { user } = useAuth();
  const { executives } = useFilters();
  const [month, setMonth] = useState(monthISO());
  const [targets, setTargets] = useState({});
  const [saving, setSaving] = useState(null);
  const [config, setConfig] = useState({ tiers: [] });
  const [rows, setRows] = useState([]);

  const load = async () => {
    const tr = await api.get("/targets", { params: { month } });
    const map = {};
    tr.data.forEach((t) => { map[t.executive_id] = t; });
    setTargets(map);
    const c = await api.get("/incentive-config");
    setConfig(c.data || { tiers: [] });
    const tva = await api.get("/dashboard/target-vs-actual", { params: { month } });
    setRows(tva.data.rows);
  };

  useEffect(() => { load(); }, [month]);

  const getVal = (execId, k) => {
    const v = targets[execId]?.[k];
    if (v === undefined || v === null) return emptyTarget[k];
    return v;
  };

  const setVal = (execId, k, v) => {
    setTargets((prev) => ({
      ...prev,
      [execId]: { ...(prev[execId] || { executive_id: execId, month, ...emptyTarget }), [k]: Number(v) },
    }));
  };

  const saveTarget = async (execId) => {
    setSaving(execId);
    try {
      const body = { executive_id: execId, month, ...emptyTarget, ...(targets[execId] || {}) };
      body.executive_id = execId; body.month = month;
      await api.post("/targets", body);
      toast.success("Targets saved");
      load();
    } catch { toast.error("Failed"); } finally { setSaving(null); }
  };

  const saveTiers = async () => {
    try {
      await api.put("/incentive-config", { id: "default", executive_id: null, tiers: config.tiers });
      toast.success("Incentive slabs updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Governance</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Target Setting &amp; Incentive Calculator
          </h2>
        </div>
        <div className="text-white text-3xl font-light" style={{ fontFamily: "Outfit" }}>
          Target Setting &amp; Incentive Calculator
        </div>
        <input data-testid="targets-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-1.5 text-sm outline-none" />
      </div>

      {/* Target table */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden" data-testid="target-editor">
        <div className="p-4 border-b border-slate-800">
          <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Monthly Targets — {month}</div>
          <div className="text-xs text-slate-400 mt-1">Weekly/Daily auto-suggested as Monthly ÷ 4 and Monthly ÷ 26.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/40 text-slate-300 text-[11px] uppercase tracking-wider">
                <th className="text-left px-3 py-3">Metric</th>
                {executives.map((e) => (
                  <th key={e.id} className="text-left px-3 py-3">{e.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map(([k, label, big]) => (
                <tr key={k} className="border-t border-slate-800">
                  <td className="px-3 py-2.5 text-slate-300">{label}</td>
                  {executives.map((e) => (
                    <td key={e.id} className="px-3 py-2.5">
                      <input
                        data-testid={`target-${k}-${e.id}`}
                        type="number"
                        value={getVal(e.id, k)}
                        onChange={(ev) => setVal(e.id, k, ev.target.value)}
                        className="w-32 bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]"
                      />
                      {big && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          Weekly ~{Math.round(getVal(e.id, k) / 4).toLocaleString("en-IN")} • Daily ~{Math.round(getVal(e.id, k) / 26).toLocaleString("en-IN")}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-slate-800">
                <td></td>
                {executives.map((e) => (
                  <td key={e.id} className="px-3 py-3">
                    <button data-testid={`save-target-${e.id}`} onClick={() => saveTarget(e.id)} disabled={saving === e.id}
                      className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5">
                      {saving === e.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Incentive slabs */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-6" data-testid="incentive-config">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-white font-medium" style={{ fontFamily: "Outfit" }}>Incentive Slabs (Revenue-based)</div>
            <div className="text-xs text-slate-400">Below the lowest threshold = ₹0. Payout escalates as revenue crosses each threshold.</div>
          </div>
          {user.role === "founder" && (
            <button data-testid="save-tiers" onClick={saveTiers}
              className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5">
              <Save size={12} /> Save Slabs
            </button>
          )}
        </div>
        <div className="space-y-2">
          {config.tiers?.map((t, i) => (
            <div key={i} className="flex items-center gap-3 bg-slate-900/60 border border-slate-800 rounded-lg p-3">
              <div className="text-xs text-slate-400 w-16">Above</div>
              <input type="number" value={t.threshold}
                onChange={(e) => {
                  const nt = [...config.tiers]; nt[i] = { ...nt[i], threshold: Number(e.target.value) };
                  setConfig({ ...config, tiers: nt });
                }}
                disabled={user.role !== "founder"}
                className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-sm w-40 outline-none focus:ring-2 focus:ring-[#C9A24B]" />
              <div className="text-xs text-slate-400">→ Payout ₹</div>
              <input type="number" value={t.amount}
                onChange={(e) => {
                  const nt = [...config.tiers]; nt[i] = { ...nt[i], amount: Number(e.target.value) };
                  setConfig({ ...config, tiers: nt });
                }}
                disabled={user.role !== "founder"}
                className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-sm w-32 outline-none focus:ring-2 focus:ring-[#C9A24B]" />
              {user.role === "founder" && (
                <button onClick={() => setConfig({ ...config, tiers: config.tiers.filter((_, j) => j !== i) })}
                  className="text-rose-400 hover:text-rose-300"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
          {user.role === "founder" && (
            <button onClick={() => setConfig({ ...config, tiers: [...(config.tiers || []), { threshold: 0, amount: 0 }] })}
              className="flex items-center gap-1.5 text-xs text-[#C9A24B] hover:text-[#b08d40]"><Plus size={12} /> Add slab</button>
          )}
        </div>
      </div>

      {/* Live projection */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-6" data-testid="live-projection">
        <div className="text-white font-medium mb-4" style={{ fontFamily: "Outfit" }}>Live Incentive Projection ({month})</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <div key={r.executive_id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-white font-medium">{r.executive_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]">Projected</div>
              </div>
              <div className="mt-3 text-emerald-400 text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {fmtINR(r.incentive_projected)}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                On revenue {fmtINR(r.actual.revenue)} of target {fmtINR(r.target.revenue_monthly || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
